<template>
	<input
		v-bind="$attrs"
		ref="input"
		v-model="localValue"
		type="checkbox"
		:class="['color-' + color]"
		:required="required"
		:style="'font-size: ' + size + 'px'"
		class="focus:ring-2 focus:ring-offset-2 ring-offset-gray-800 focus:ring-gray-500"
		@change="onChange"
	/>
</template>

<script>
export default {
	props: {
		modelValue: {
			type: [Boolean, String],
		},
		color: {
			default: () => 'blue',
			type: String,
			validator: color => ['blue', 'green', 'red'].includes(color),
		},
		size: {
			default: () => 14,
			type: Number,
		},
		required: Boolean,
		indeterminate: Boolean,
	},
	emits: ['update:modelValue', 'input'],
	data() {
		return {
			localValue: this.modelValue,
		}
	},
	watch: {
		indeterminate: {
			immediate: true,
			async handler(indeterminate) {
				await this.$nextTick()
				if (!this.$refs.input) return
				this.$refs.input.indeterminate = indeterminate
				this.localValue = this.modelValue
			},
		},
		modelValue(value) {
			this.localValue = value
		},
	},
	methods: {
		onChange(event) {
			this.$emit('update:modelValue', event.target.checked)
			this.$emit('input', event.target.checked)
		},
	},
}
</script>

<style scoped>
input[type='checkbox'] {
	-webkit-appearance: none;
	appearance: none;
	background-color: transparent;
	cursor: pointer;
	margin: 0;
	margin-top: 0.2em;
	font: inherit;
	color: currentColor;
	width: 1em;
	height: 1em;
	border: 0.1em solid currentColor;
	border-radius: 0.25em;
	transform: translateY(-0.075em);
	display: grid;
	place-content: center;
	overflow: hidden;
	flex-shrink: 0;

	--accent-color: currentColor;
}

input[type='checkbox']:hover {
	border-color: color-mix(in oklch, currentColor 100%, white 25%);
}

input[type='checkbox']:checked::before {
	content: '';
	width: 0.6em;
	height: 0.5em;
	box-shadow: inset 1em 1em var(--gray-800);
	clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
}

.dark input[type='checkbox']:checked::before {
	box-shadow: inset 1em 1em var(--gray-100);
}

input[type='checkbox']:indeterminate::before {
	width: 0.6em;
	height: 0.1em;
	content: '-';
	box-shadow: inset 0 0.1em var(--gray-800);
}

.dark input[type='checkbox']:indeterminate::before {
	box-shadow: inset 0 0.1em var(--gray-100);
}

input[type='checkbox']:checked,
input[type='checkbox']:indeterminate {
	border-color: var(--accent-color);
	background-color: var(--accent-color);
}

input[type='checkbox']:disabled {
	cursor: not-allowed;
	border-color: var(--gray-600);
}

input[type='checkbox'].color-blue {
	--accent-color: var(--blue-500);
}

input[type='checkbox'].color-green {
	--accent-color: var(--green-500);
}

input[type='checkbox'].color-red {
	--accent-color: var(--red-500);
}
</style>
